import React, { useState, useEffect, useCallback } from 'react';
import api from "../api";   // adjust path as needed
import { useParams } from 'react-router-dom';
import { useClientPagination } from '../hooks/useClientPagination';
import TablePagination from '../components/TablePagination';
import { formatDateOnly } from '../utils/formatDate';

const ScanCheck = () => {
    const { domain: paramDomain } = useParams();
    const [domain, setDomain] = useState(paramDomain || '');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Extract domain from URL on mount
    useEffect(() => {
        if (paramDomain) {
            setDomain(paramDomain);
        }
    }, [paramDomain]);

    const performScan = useCallback(async (targetDomain) => {
        if (!targetDomain) return;

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            // Add timestamp to prevent 304 caching
            //const response = await axios.get(`/api/scan?domain=${encodeURIComponent(targetDomain)}&t=${Date.now()}`);
            const response = await api.get(`/api/scan`, {
                params: {
                    domain: targetDomain,
                    t: Date.now(),
                }
            });
            setResult(response.data);
        } catch (err) {
            console.error('Scan failed:', err);
            const errorMessage = err.response?.data?.error || err.message || "Failed to connect to scanning server.";
            setError(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : String(errorMessage));
        } finally {
            setLoading(false);
        }
    }, []);

    // Trigger scan automatically if domain is present
    useEffect(() => {
        if (domain && !result && !loading && !error) {
            performScan(domain);
        }
    }, [domain, performScan, result, loading, error]);

    const ciphersPag = useClientPagination(result?.ciphers || [], domain);

    // --- Helpers ---


    const getGradeBadgeColor = (grade) => {
        if (!grade) return 'bg-secondary';
        if (grade === 'A' || grade === 'A-') return 'bg-success';
        if (grade === 'B') return 'bg-warning';
        if (grade === 'C') return 'bg-info';
        return 'bg-danger';
    };

    const formatDate = (dateString) => formatDateOnly(dateString);

    const isCertificateValid = (certificate) => {
        if (!certificate || !certificate.valid_to) return false;
        try {
            const expire = new Date(certificate.valid_to);
            return expire > new Date();
        } catch {
            return false;
        }
    };

    const getCipherName = (cipher) => {
        if (!cipher) return 'Unknown';
        if (typeof cipher === 'string') return cipher;
        if (typeof cipher === 'object' && cipher !== null) {
            return cipher.name || cipher.standardName || 'Unknown Cipher';
        }
        return 'Unknown';
    };

    // --- Render ---
    return (
        <div className="container bg-light min-vh-100 py-4">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-0">
                                <h4 className="mb-0">
                                    Security Audit Scan for:{" "}
                                    <span className="text-primary">{domain || "Loading..."}</span>
                                </h4>
                                <a href="/scans" className="btn btn-outline-dark btn-sm">
                                    ← Back to My Scans
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary mb-3 w-[3rem] h-[3rem]">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <h5 className="text-muted">Running Security Scan...</h5>
                                <p className="text-muted mb-0">Scanning: {domain}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center shadow-sm">
                            <i className="las la-exclamation-triangle la-2x me-3"></i>
                            <div>
                                <h5 className="alert-heading mb-1">Scan Failed</h5>
                                <p className="mb-0">{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Results Dashboard */}
            {result && !loading && (
                <div className="row g-4">
                    {/* Summary Card */}
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body">
                                <div className="row align-items-center">
                                    <div className="col-md-8">
                                        <h2 className="card-title text-dark mb-1">{result.domain}</h2>
                                        <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
                                            <span className={`badge ${result.fetchedHtml ? 'bg-success' : 'bg-warning'}`}>
                                                <i className="las la-code me-1"></i>
                                                {result.fetchedHtml ? "HTML Accessible" : "No HTML Access"}
                                            </span>
                                            {result.negotiated?.protocol && (
                                                <span className="badge bg-info">
                                                    <i className="las la-lock me-1"></i>
                                                    Active: {result.negotiated.protocol}
                                                </span>
                                            )}
                                            <span className="badge bg-secondary">
                                                <i className="las la-key me-1"></i>
                                                {getCipherName(result.negotiated?.cipher)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-md-4 text-md-end">
                                        <div className="d-flex align-items-center justify-content-md-end gap-3">
                                            <div className="text-end">
                                                <small className="text-muted d-block">SECURITY GRADE</small>
                                                <span className="text-muted small">{result.negotiated?.protocol || "Unknown"}</span>
                                            </div>
                                            <div className={`badge ${getGradeBadgeColor(result.grade)} rounded-circle d-flex align-items-center justify-content-center`}
                                                  style={{ fontWeight: 'bold' }}>
                                                {result.grade}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Certificate Information */}
                    <div className="col-md-6">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-primary text-white">
                                <h5 className="card-title mb-0">
                                    <i className="las la-certificate me-2"></i>
                                    SSL/TLS Certificate
                                </h5>
                            </div>
                            <div className="card-body">
                                {result.certificate ? (
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <label className="form-label text-muted small mb-1">Subject (CN)</label>
                                            <p className="fw-bold text-truncate" title={result.certificate.subject?.CN}>
                                                {result.certificate.subject?.CN || 'N/A'}
                                            </p>
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label text-muted small mb-1">Issuer</label>
                                            <p className="text-truncate" title={result.certificate.issuer?.CN}>
                                                {result.certificate.issuer?.CN || 'N/A'}
                                            </p>
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label text-muted small mb-1">Valid From</label>
                                            <p className="small">{formatDate(result.certificate.valid_from)}</p>
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label text-muted small mb-1">Valid To</label>
                                            <p className={`small ${isCertificateValid(result.certificate) ? 'text-success' : 'text-danger'}`}>
                                                {formatDate(result.certificate.valid_to)}
                                            </p>
                                        </div>
                                        <div className="col-12">
                                            <span className={`badge ${isCertificateValid(result.certificate) ? 'bg-success' : 'bg-danger'}`}>
                                                <i className={`las ${isCertificateValid(result.certificate) ? 'la-check' : 'la-times'} me-1`}></i>
                                                {isCertificateValid(result.certificate) ? 'Valid Certificate' : 'Invalid Certificate'}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-muted fst-italic">No certificate information available</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Protocol Support */}
                    <div className="col-md-6">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-info text-white">
                                <h5 className="card-title mb-0">
                                    <i className="las la-shield-alt me-2"></i>
                                    Protocol Support
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-2">
                                    {result.protocols && Object.entries(result.protocols).map(([protocol, supported]) => (
                                        <div key={protocol} className="col-12">
                                            <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                                                <span className="fw-medium">{protocol}</span>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className={`badge ${supported ? 'bg-success' : 'bg-danger'}`}>
                                                        {supported ? 'Supported' : 'Not Supported'}
                                                    </span>
                                                    <i className={`las ${supported ? 'la-check text-success' : 'la-times text-danger'}`}></i>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {result.negotiated?.cipher && (
                                    <div className="mt-3 pt-3 border-top">
                                        <label className="form-label text-muted small mb-1">Negotiated Cipher</label>
                                        <code className="d-block bg-light p-2 rounded small text-break">
                                            {getCipherName(result.negotiated.cipher)}
                                        </code>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Content Analysis */}
                    <div className="col-md-6">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-warning text-dark">
                                <h5 className="card-title mb-0">
                                    <i className="las la-file-code me-2"></i>
                                    Content Analysis
                                </h5>
                            </div>
                            <div className="card-body">
                                {result.fetchedHtml ? (
                                    result.htmlIssues && result.htmlIssues.length > 0 ? (
                                        <div>
                                            <p className="text-muted small mb-3">
                                                Found {result.htmlIssues.length} potential issue(s) in the HTML content
                                            </p>
                                            <div className="list-group list-group-flush">
                                                {result.htmlIssues.map((issue, idx) => (
                                                    <div key={idx} className="list-group-item px-0">
                                                        <div className="d-flex align-items-start">
                                                            <i className="las la-exclamation-triangle text-danger mt-1 me-2"></i>
                                                            <span className="small">{issue}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-3">
                                            <i className="las la-check-circle text-success la-2x mb-2"></i>
                                            <p className="text-success mb-0">No suspicious content or obfuscation detected</p>
                                        </div>
                                    )
                                ) : (
                                    <div className="text-center py-3">
                                        <i className="las la-exclamation-triangle text-warning la-2x mb-2"></i>
                                        <p className="text-warning mb-1">Could not fetch HTML content</p>
                                        <p className="text-muted small">Site may be blocking requests or unavailable</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Cipher Suite Support */}
                    <div className="col-md-6">
                        <div className="card shadow-sm h-100">
                            <div className="card-header bg-success text-white">
                                <h5 className="card-title mb-0">
                                    <i className="las la-server me-2"></i>
                                    Cipher Suite Support
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th className="ps-3">Cipher Name</th>
                                                <th className="text-end pe-3">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ciphersPag.items.map((cipher, idx) => (
                                                <tr key={`${cipher.cipher}-${idx}`}>
                                                    <td className="ps-3">
                                                        <code className="small text-truncate d-block max-w-[200px]"
                                                            title={cipher.cipher}>
                                                            {cipher.cipher}
                                                        </code>
                                                    </td>
                                                    <td className="text-end pe-3">
                                                        {cipher.ok ? (
                                                            <span className="badge bg-success">
                                                                <i className="las la-check me-1"></i>Accepted
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-secondary">
                                                                <i className="las la-times me-1"></i>Refused
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <TablePagination {...ciphersPag} onPageChange={ciphersPag.setCurrentPage} className="px-3" />
                                {result.ciphers && (
                                    <div className="card-footer bg-light">
                                        <small className="text-muted">
                                            {result.ciphers.filter(c => c.ok).length} of {result.ciphers.length} ciphers supported
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Security Recommendations */}
                    <div className="col-12">
                        <div className="card shadow-sm border-primary">
                            <div className="card-header bg-light border-primary">
                                <h5 className="card-title mb-0 text-primary">
                                    <i className="las la-lightbulb me-2"></i>
                                    Security Recommendations
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    {result.protocols?.["TLSv1.0"] && (
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start">
                                                <i className="las la-exclamation-circle text-danger mt-1 me-2"></i>
                                                <div>
                                                    <strong className="text-danger">Critical:</strong>
                                                    <p className="mb-0 small">Disable TLS 1.0 - this protocol is insecure and deprecated</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {result.protocols?.["TLSv1.1"] && (
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start">
                                                <i className="las la-exclamation-triangle text-warning mt-1 me-2"></i>
                                                <div>
                                                    <strong className="text-warning">Warning:</strong>
                                                    <p className="mb-0 small">Consider disabling TLS 1.1 - this protocol is deprecated</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {!result.protocols?.["TLSv1.3"] && (
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start">
                                                <i className="las la-info-circle text-info mt-1 me-2"></i>
                                                <div>
                                                    <strong className="text-info">Recommendation:</strong>
                                                    <p className="mb-0 small">Enable TLS 1.3 for better security and performance</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {result.htmlIssues && result.htmlIssues.length > 0 && (
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start">
                                                <i className="las la-code text-warning mt-1 me-2"></i>
                                                <div>
                                                    <strong className="text-warning">Content Issues:</strong>
                                                    <p className="mb-0 small">Review HTML content for potential security concerns</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {result.ciphers && result.ciphers.filter(c => c.ok).length < 4 && (
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-start">
                                                <i className="las la-lock text-info mt-1 me-2"></i>
                                                <div>
                                                    <strong className="text-info">Cipher Suites:</strong>
                                                    <p className="mb-0 small">Consider enabling more modern cipher suites</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScanCheck;